import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { ControlTypeEnum, SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { asapScheduler, first, skip } from 'rxjs';

@Component({
  selector: 'app-radio-input-group',
  templateUrl: './radio-input-group.component.html',
  styleUrls: ['./radio-input-group.component.css']
})
export class RadioInputGroupComponent implements OnInit {

  @Input() indication: IndicationDefinition;
  @Input() labelName: string = 'Select Criteria:';
  @Input() controlName: string = 'criteria';
  @Input() controlDisplayName: string = 'Criteria';
  @Input() hasCustomControlParent: boolean = false;

  @Output() indicationSelectionChanged: EventEmitter<SelectionsModel> = new EventEmitter();
  
  allIndications: IndicationGroup[] = [];

  // checkboxValue: any;

  public formGroup = new FormGroup({
    radioSelection: new FormControl('', [
      Validators.required
    ]),
    checkboxSelection: new FormControl('MC', [])
  });
  constructor(private sharedService: SharedService) { }

  ngOnInit() {
    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });
    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data && !this.hasCustomControlParent) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections.filter(i => i.controlName == this.controlName);
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      const indicationControlSpecificSelections = this.indication.selections.filter(i => i.controlType === ControlTypeEnum.RADIO || i.controlType === ControlTypeEnum.RADIO_WITH_CHECKBOX);
      if (indicationControlSpecificSelections && indicationControlSpecificSelections.length > 0) {
        asapScheduler.schedule(() => {
          if (indicationControlSpecificSelections[0].selectionValue.includes('_')) {
            const checkboxParentInputOption = this.indication.inputOptions.find(i => i.hasOwnProperty('checkbox'));
            this.formGroup.controls.radioSelection.setValue(checkboxParentInputOption.name);
            this.formGroup.controls.checkboxSelection.setValue(indicationControlSpecificSelections[0].selectionValue);
          } else {
          this.formGroup.controls.radioSelection.setValue(indicationControlSpecificSelections[0].selectionValue);
          this.updateSelections();
          }
        });
      }
    } else {
      this.setDefaults();
    }
  }

  setDefaults() {
    this.overrideInputValue(this.indication.inputOptions[0].defaultValue);
  }

  overrideInputValue(value) {
    asapScheduler.schedule(() => {
      if (value.includes('_')) {
        const checkboxParentInputOption = this.indication.inputOptions.find(i => i.hasOwnProperty('checkbox'));
        this.formGroup.controls.radioSelection.setValue(checkboxParentInputOption.name);
        this.formGroup.controls.checkboxSelection.setValue(value);
      } else {
        this.formGroup.controls.radioSelection.setValue(value);
      }
      this.updateSelections();
    });
  }

  enableCheckbox(): boolean {
    const checkboxParentInputOption = this.indication.inputOptions.find(i => i.hasOwnProperty('checkbox'));
    if (checkboxParentInputOption && (this.formGroup.controls.radioSelection.value === checkboxParentInputOption.name ||
        this.formGroup.controls.radioSelection.value === checkboxParentInputOption['checkbox'].name)) {
          return true;
        }
    return false;
  }

  updateSelections(): void {
    let selectionValue = '';
    let selectionDisplayName = '';
    const radioSelectionValue = this.formGroup.controls.radioSelection.value;
    const checkboxSelectionValue = this.formGroup.controls.checkboxSelection.value;      
    // show & with checkbox value (aka now sub-radio button group value) ---- don't show & if the radio and sub-radio are the same select ('MC')
    if (this.enableCheckbox() && checkboxSelectionValue && radioSelectionValue && checkboxSelectionValue !== radioSelectionValue) {
      const checkboxParentInputOption = this.indication.inputOptions.find(i => i.hasOwnProperty('checkbox'));
      selectionValue = this.formGroup.controls.checkboxSelection.value;
      selectionDisplayName += `${checkboxParentInputOption.displayName} & 
      ${checkboxParentInputOption['checkbox'].find(i => i.name === this.formGroup.controls.checkboxSelection.value).displayName}`;
    } else {
      selectionValue = this.formGroup.controls.radioSelection.value;
      selectionDisplayName = this.indication.inputOptions.find(i => i.name === this.formGroup.controls.radioSelection.value).displayName;
    }
    const currentSelection: SelectionsModel = {
      indicationName: this.indication.name,
      controlName: this.controlName,
      controlType: ControlTypeEnum.RADIO,
      controlDisplayName: this.controlDisplayName,
      order: 1,
      selectionValue: selectionValue,
      selectionDisplayName: selectionDisplayName
    };
    if (this.hasCustomControlParent) {
      this.indicationSelectionChanged.emit(currentSelection);
    } else {
      this.indication.selections = [currentSelection];
      const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
      const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
      const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
      this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
      this.sharedService.setAllIndications(this.allIndications);
    }
  }
}
